import { useSystem } from 'api/systems/systems.api'
import { useUnits } from 'api/units/units.api'
import React, { ReactElement, useMemo } from 'react'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import { DigitalTwinAccumulatorLevels } from 'ui/components/DigitalTwinAccumulatorLevels/DigitalTwinAccumulatorLevels'
import { OptModelsAccumulatorLevels } from 'ui/components/OptModelAccumulatorLevels/OptModelsAccumulatorLevels'
import { useOptModelOptimizationContext } from '../OptModelOptimizationContext'

function getModelIds(activeSystem?: System): { digitalTwinUid?: number; optModelId?: number } {
  // If activeSystem is undefined, we don't want to show any accumulator levels (optModelId and digitalTwinUid will be undefined).
  // This happens while the system is being loaded.
  return {
    digitalTwinUid: activeSystem?.primary_digital_twin?.uid,
    optModelId: activeSystem?.primary_opt_model?.id,
  }
}

export default function AccumulatorLevels(): ReactElement | null {
  const { systemId } = useAuth()
  const {
    data: activeSystem,
    status: loadingSystemStatus,
    fetchStatus: loadingSystemFetchStatus,
  } = useSystem({ id: systemId })
  const { setIsValid, setUnitSettings } = useOptModelOptimizationContext()

  const { digitalTwinUid, optModelId } = useMemo(() => getModelIds(activeSystem), [activeSystem])

  // Fetch units data to determine if we should show OptModelsAccumulatorLevels
  const { data: units = [] } = useUnits({
    optModel: optModelId,
    unit_type: 'Storage',
    enabled: !digitalTwinUid,
  })

  // Show loading state while system is being fetched
  if (loadingSystemStatus === 'pending' || loadingSystemFetchStatus === 'fetching') {
    return null
  }

  // Don't render if there's nothing to show
  const hasOptModelAccumulators = optModelId !== undefined && optModelId !== null && units.length > 0
  const hasDigitalTwinAccumulators = digitalTwinUid !== undefined && digitalTwinUid !== null

  if (!hasOptModelAccumulators && !hasDigitalTwinAccumulators) {
    return null
  }

  return (
    <div className="flex flex-row">
      {hasOptModelAccumulators && (
        <OptModelsAccumulatorLevels setIsValid={setIsValid} setUnitSettings={setUnitSettings} optModelId={optModelId} />
      )}

      {hasDigitalTwinAccumulators && <DigitalTwinAccumulatorLevels digitalTwinUid={digitalTwinUid} />}
    </div>
  )
}
